import {Card, Typography, Box, Tooltip, useMediaQuery, useTheme, CircularProgress} from "@mui/material";
import {useState, useEffect, useMemo, useRef} from "react";
import UsageRateTooltip from "./tooltips/usageratetooltip";
import {DateRange, DateRangeProps, UsageRateType} from "../api/types";
import {useGetDashboardCounts} from "../api/hooks";
import { DUMMY_DASHBOARD_COUNTS, DUMMY_USAGE_RATE } from "./walkthrough-dummy-data";

const rankColors = [
    "#1a1464", // 1st
    "#123a8c", // 2nd
    "#1565c0", // 3rd
    "#1689c9", // 4th
    "#14b6c9", // 5th
    "#4fc3dc", // 6th
    "#81e9ff", // 7th
    "#c8f3f7", // 8th
];

const hexToRgb = (hex: string) => {
    const h = hex.replace("#", "");
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgbToHex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const lerpColor = (aHex: string, bHex: string, t: number) => {
    const a = hexToRgb(aHex);
    const b = hexToRgb(bHex);
    return rgbToHex(lerp(a.r, b.r, t), lerp(a.g, b.g, t), lerp(a.b, b.b, t));
};

const getRankColor = (index: number, count: number, palette: string[]) => {
    if (count <= 1) return palette[0];
    if (palette.length === 1) return palette[0];

    const pos = (index / (count - 1)) * (palette.length - 1);
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, palette.length - 1);
    const t = pos - i0;

    return lerpColor(palette[i0], palette[i1], t);
};

const getLastMonthRange = (): DateRange => {
    const now = new Date();

    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

    return {
        startDate: start,
        endDate: end,
    };
};

const UsageRateCard = ({dateRange, isWalkthroughActive}: DateRangeProps & { isWalkthroughActive?: boolean }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("laptop"));

    const effectiveDateRange = dateRange ?? getLastMonthRange();

    const {data: realApiData, isLoading: realLoading} = useGetDashboardCounts(effectiveDateRange);
    const apiData = isWalkthroughActive ? DUMMY_DASHBOARD_COUNTS : realApiData;
    const isLoading = isWalkthroughActive ? false : realLoading;

    const [animate, setAnimate] = useState(false);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const [atBottom, setAtBottom] = useState(false);

    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setAnimate(false);
            return;
        }

        const t = setTimeout(() => setAnimate(true), 50);
        return () => clearTimeout(t);
    }, [isLoading]);

    const calculateMarketShare = (value: number, total: number, decimals = 1): string => {
        if (total <= 0) return "0%";

        return `${((value / total) * 100).toFixed(decimals)}%`;
    };
    const totalTransactions = (apiData?.hihVisitCount ?? 0) + (apiData?.konsultaSubmittedCount ?? 0);

    const data: UsageRateType[] = useMemo(() => {
        if (isWalkthroughActive) {
            return DUMMY_USAGE_RATE.map((item, index) => ({
                ...item,
                color: getRankColor(index, DUMMY_USAGE_RATE.length, rankColors),
            }));
        }

        if (!apiData) return [];

        const base = [
            {
                label: "HIH",
                value: apiData.hihVisitCount,
                marketShare: calculateMarketShare(apiData.hihVisitCount, totalTransactions),
            },
            {
                label: "Konsulta",
                value: apiData.konsultaSubmittedCount,
                marketShare: calculateMarketShare(apiData.konsultaSubmittedCount, totalTransactions),
            },
        ];

        const sorted = [...base].sort((a, b) => b.value - a.value);

        let currentRank = 0;
        let lastValue: number | null = null;

        return sorted.map((item, index) => {
            if (item.value !== lastValue) currentRank += 1;
            lastValue = item.value;

            const ordinal =
                currentRank === 1 ? "1st" : currentRank === 2 ? "2nd" : currentRank === 3 ? "3rd" : `${currentRank}th`;

            const isShared = sorted.filter((d) => d.value === item.value).length > 1;

            return {
                ...item,
                ranking: isShared ? `${ordinal} Place – Shared Rank` : `${ordinal} Place`,
                isShared,
                color: getRankColor(index, sorted.length, rankColors),
            };
        });
    }, [apiData, isWalkthroughActive]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const hasOverflow = el.scrollHeight > el.clientHeight;
        setShowScrollIndicator(hasOverflow);
        setAtBottom(false);
    }, [data]);
    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;

        setAtBottom(isAtBottom);
    };

    const minValue = Math.min(...data.map((d) => d.value));
    const maxValue = Math.max(...data.map((d) => d.value));
    const ratio = minValue === 0 ? Infinity : maxValue / minValue;

    return (
        <Card
            id="dashboard-usage-rate-card"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                position: "relative",
                p: 3,
                borderRadius: 3,
                backgroundColor: "#eef3ff",
                minHeight: 470,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0px 4px 6px rgba(15, 23, 42, 0.18)",
            }}
        >
            <Typography fontWeight={600} mb={1} fontSize={20} textAlign="left" color="primary.main">
                Total Product Usage Rate
            </Typography>

            <Box display="flex" mb={1} color="text.secondary" paddingInline={8}>
                <Box sx={{width: 50}}>
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                        Product
                    </Typography>
                </Box>

                <Box sx={{flex: 1}} />

                <Box sx={{width: 125, textAlign: "center"}}>
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                        Transactions
                    </Typography>
                </Box>
            </Box>

            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    pr: 1,
                    "&::-webkit-scrollbar": {
                        width: isHovered ? 6 : 0,
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: isHovered ? "rgba(15,23,42,0.25)" : "transparent",
                        borderRadius: 3,
                        opacity: isHovered ? 1 : 0,
                        transition: "opacity 0.25s ease",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                    },
                    scrollbarColor: isHovered ? "rgba(15,23,42,0.25) transparent" : "transparent transparent",
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                        }}
                    >
                        <CircularProgress size={36} thickness={4} />

                        <Typography fontSize={14} fontWeight={600} color="text.secondary">
                            Fetching Data...
                        </Typography>
                    </Box>
                ) : data.length === 0 ? (
                    <Box
                        sx={{
                            height: "350px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "center",
                                color: "#6D87AA",
                                fontSize: "18px",
                                fontWeight: 500,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <img src="/empty-table-data-logo.svg" alt="No Data" style={{maxHeight: 300}} />
                            <Box
                                component="span"
                                sx={{
                                    fontFamily: "Poppins",
                                    fontWeight: 600,
                                    fontSize: "24px",
                                    color: "#A1A1A1",
                                    textAlign: "center",
                                }}
                            >
                                {"No Data Found"}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {data.map((item, index) => {
                            const ZERO_BAR_WIDTH_PX = 10;

                            const widthPct =
                                item.value === 0
                                    ? null
                                    : ratio > 10
                                    ? (Math.log10(item.value + 1) / Math.log10(maxValue + 1)) * 100
                                    : (item.value / maxValue) * 100;

                            const isTopFour = index < 4;

                            let borderRadius = "0 0 8px 0";

                            if (index === 0) {
                                borderRadius = item.isShared ? "8px 8px 8px 0" : "8px 8px 0 0";
                            } else if (item.isShared) {
                                borderRadius = index === data.length - 1 ? "0 8px 8px 8px" : "0 8px 8px 0";
                            }
                            return (
                                <Box
                                    key={item.label}
                                    paddingInline={8}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(0, 1fr) 80px",
                                        alignItems: "center",
                                        height: 44,
                                    }}
                                >
                                    <Tooltip
                                        disableInteractive
                                        key={item.label}
                                        placement={isSmallScreen ? "bottom" : "right"}
                                        arrow
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: "offset",
                                                    options: {
                                                        offset: [0, isSmallScreen ? 0 : 50],
                                                    },
                                                },
                                            ],
                                        }}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: "transparent",
                                                    boxShadow: "none",
                                                    padding: 0,
                                                },
                                            },
                                            arrow: {
                                                sx: {
                                                    color: "#020617",
                                                },
                                            },
                                        }}
                                        title={
                                            <UsageRateTooltip
                                                label={item.label}
                                                value={item.value}
                                                marketShare={item.marketShare}
                                                ranking={item.ranking}
                                            />
                                        }
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 10,
                                                height: "100%",
                                                width: animate
                                                    ? item.value === 0
                                                        ? `${ZERO_BAR_WIDTH_PX}px`
                                                        : `${Math.max(widthPct!, 3)}%`
                                                    : "0%",
                                                transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                                                position: "relative",
                                                "&:hover .moving-bar": {
                                                    transform: "translate3d(3px, 0, 0)",
                                                    boxShadow: "0 12px 32px rgba(30,64,175,0.45)",
                                                    filter: "brightness(1.08)",
                                                },
                                            }}
                                        >
                                            <Box
                                                className="moving-bar"
                                                sx={{
                                                    height: "100%",
                                                    backgroundColor: item.color,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    px: 2,
                                                    borderRadius,
                                                    transition:
                                                        "transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease",
                                                    willChange: "transform",
                                                    transformOrigin: "left center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: isTopFour ? "#fff" : "#000",
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "50%",
                                            textAlign: "center",
                                            fontWeight: 600,
                                            color: "#111",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: "primary.main",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.value.toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </>
                )}
            </Box>
            {showScrollIndicator && !atBottom && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 48,
                        pointerEvents: "none",
                        background: "linear-gradient(180deg, rgba(238,243,255,0) 0%, #eef3ff 85%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        pb: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: "primary.main",
                            opacity: 0.75,
                        }}
                    >
                        ↓ Scroll for more
                    </Typography>
                </Box>
            )}
        </Card>
    );
};

export default UsageRateCard;
